import CloseIcon from "@mui/icons-material/Close";
import { PositionRelationshipLegend } from "./PositionRelationshipLegend";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import {
  REVIEW_PRESET_PRESENTATION,
  type ReviewBoardSettings,
  type ReviewBoardSettingsPatch,
} from "./review-board-settings";

interface Props {
  open: boolean;
  settings: ReviewBoardSettings;
  onChange: (settings: ReviewBoardSettingsPatch) => void;
  onClose: () => void;
  context?: "review" | "pattern" | "board" | "nikola";
}

export const ReviewBoardSettingsDialog = ({ open, settings, onChange, onClose, context = "review" }: Props) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      transitionDuration={0}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 3 },
          maxHeight: "calc(100dvh - 16px)",
          borderRadius: 0,
          bgcolor: "background.default",
          backgroundImage: "none",
          border: "1px solid",
          borderColor: "primary.main",
          boxShadow: "8px 8px 0 #ff3d12",
        },
      }}
    >
      <DialogTitle
        sx={{
          minHeight: 68,
          p: 0,
          display: "grid",
          gridTemplateColumns: "8px 1fr 52px",
          alignItems: "stretch",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ bgcolor: "secondary.main" }} />
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography
            component="span"
            sx={{
              display: "block",
              color: "text.secondary",
              fontSize: ".6rem",
              fontWeight: 900,
              letterSpacing: ".16em",
            }}
          >
            {{ pattern: "PATTERNS", review: "GAME REVIEW", board: "BOARD", nikola: "NIKOLA" }[context]} / DISPLAY &
            VERIFICATION
          </Typography>
          <Typography
            component="span"
            sx={{
              display: "block",
              color: "primary.main",
              fontSize: "1.35rem",
              fontWeight: 950,
              letterSpacing: "-.04em",
            }}
          >
            Board settings
          </Typography>
        </Box>
        <IconButton
          aria-label="Close board settings"
          onClick={onClose}
          sx={{
            borderRadius: 0,
            borderLeft: "1px solid",
            borderColor: "divider",
            color: "primary.main",
            "&:hover": { bgcolor: "secondary.main", color: "secondary.contrastText" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, overflowX: "hidden" }}>
        <ReviewBoardSettingsFields settings={settings} onChange={onChange} />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "stretch", borderTop: "1px solid", borderColor: "divider", p: 1.5 }}>
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          onClick={onClose}
          sx={{ minHeight: 48, borderRadius: 0, fontWeight: 950, letterSpacing: ".1em" }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const ReviewBoardSettingsFields = ({ settings, onChange }: Pick<Props, "settings" | "onChange">) => {
  const patch = (next: ReviewBoardSettingsPatch) => onChange(next);
  const toggle = (
    key: keyof Pick<
      ReviewBoardSettings,
      | "showLegalMoves"
      | "showMotifs"
      | "showPrincipleSignals"
      | "showOpponentAnalysis"
      | "showBestMoveArrow"
      | "showPositionMarkers"
      | "showAllPositionMarkers"
      | "moveSounds"
      | "showEvaluation"
      | "showTacticalMotifs"
    >,
  ) => patch({ [key]: !settings[key] });
  return (
    <>
      <Typography
        sx={{ mb: 0.75, color: "secondary.main", fontSize: ".64rem", fontWeight: 900, letterSpacing: ".14em" }}
      >
        01 / LOCAL VERIFICATION
      </Typography>
      <Typography sx={{ mb: 1.25, color: "text.secondary", fontSize: ".78rem" }}>
        Choose how deeply Nikola verifies the position on this device.
      </Typography>
      <Box
        role="group"
        aria-label="Evaluation depth"
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          mb: 3,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {REVIEW_PRESET_PRESENTATION.map(({ value, label, detail, icon }, index) => {
          const active = settings.evaluationPreset === value;
          return (
            <Button
              key={value}
              aria-label={`${label} · ${detail}`}
              aria-pressed={active}
              onClick={() => patch({ evaluationPreset: value })}
              sx={{
                minHeight: 78,
                minWidth: 0,
                px: 0.5,
                borderRadius: 0,
                borderInlineEnd: index < 2 ? "1px solid" : 0,
                borderColor: "divider",
                display: "grid",
                gap: 0.1,
                lineHeight: 1.1,
                bgcolor: active ? "primary.main" : "transparent",
                color: active ? "background.default" : "primary.main",
                "&:hover": { bgcolor: active ? "primary.dark" : "action.hover" },
              }}
            >
              <Typography component="span" aria-hidden sx={{ fontSize: "1.45rem", lineHeight: 1 }}>
                {icon}
              </Typography>
              <Typography component="span" sx={{ fontSize: ".72rem", fontWeight: 950, textTransform: "uppercase" }}>
                {label}
              </Typography>
              <Typography component="span" sx={{ fontSize: ".56rem", color: "inherit", opacity: 0.72 }}>
                {detail}
              </Typography>
            </Button>
          );
        })}
      </Box>
      <Typography sx={{ mb: 1, color: "secondary.main", fontSize: ".64rem", fontWeight: 900, letterSpacing: ".14em" }}>
        02 / BOARD LAYERS
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { sm: "1fr 1fr" },
          borderTop: "1px solid",
          borderLeft: "1px solid",
          borderColor: "divider",
        }}
      >
        <Setting checked={settings.moveSounds} onChange={() => toggle("moveSounds")} label="Move sounds" />
        <Setting checked={settings.showEvaluation} onChange={() => toggle("showEvaluation")} label="Evaluation bar" />
        <Setting
          checked={settings.showTacticalMotifs}
          onChange={() => toggle("showTacticalMotifs")}
          label="Tactical motifs & missed opportunities"
        />
        <Setting
          checked={settings.showLegalMoves}
          onChange={() => toggle("showLegalMoves")}
          label="Legal destinations"
          detail="Dots and capture rings after piece selection."
        />
        <Setting
          checked={settings.showMotifs}
          onChange={() => toggle("showMotifs")}
          label="Move motifs"
          detail="Verified classifications on board and timeline."
        />
        <Setting
          checked={settings.showPrincipleSignals}
          onChange={() => toggle("showPrincipleSignals")}
          label="Principle signals"
          detail="Applied and missed decision themes verified from this move."
        />
        <Setting
          checked={settings.showOpponentAnalysis}
          onChange={() => toggle("showOpponentAnalysis")}
          label="Opponent coaching"
          detail="Show their motifs, principles, and Nikola questions. Re-analyze older games to add principle signals."
        />
        <Setting
          checked={settings.showBestMoveArrow}
          onChange={() => toggle("showBestMoveArrow")}
          label="Verified arrows"
          detail="Recorded and Nikola-verified alternatives."
        />
        <Setting
          checked={settings.showPositionMarkers}
          onChange={() => toggle("showPositionMarkers")}
          label="FEN relationships"
          detail="Color-coded checks, pins, attacks, support, and slider alignments."
        />
        {settings.showPositionMarkers && (
          <Setting
            checked={settings.showAllPositionMarkers}
            onChange={() => toggle("showAllPositionMarkers")}
            label="All verified relationships"
            detail="Also show support, x-rays, and discovered-attack potential without selecting a piece."
          />
        )}
      </Box>
      <PositionRelationshipLegend settings={settings} onChange={patch} />
      <Box
        sx={{ mt: 2.5, p: 1.5, borderLeft: "4px solid", borderColor: "secondary.main", bgcolor: "background.paper" }}
      >
        <Typography sx={{ color: "text.secondary", fontSize: ".72rem", lineHeight: 1.5 }}>
          <Box component="strong" sx={{ color: "primary.main" }}>
            TRUTH BOUNDARY.
          </Box>{" "}
          Nikola presents objective, locally verified evaluation and validates every practice reply. Exact-FEN markers
          come from legal board relationships—never generated prose.
        </Typography>
      </Box>
    </>
  );
};

const Setting = ({
  checked,
  onChange,
  label,
  detail,
  compact = false,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  detail?: string;
  compact?: boolean;
}) => (
  <Box
    sx={{
      minWidth: 0,
      p: compact ? 1 : 1.25,
      borderRight: "1px solid",
      borderBottom: "1px solid",
      borderColor: "divider",
      bgcolor: checked ? "rgba(240,236,226,.08)" : "transparent",
    }}
  >
    <ButtonBase
      role="switch"
      data-tour={label}
      aria-checked={checked}
      onClick={onChange}
      disableRipple
      sx={{
        width: "100%",
        minHeight: 44,
        justifyContent: "flex-start",
        gap: 1,
        color: "text.primary",
        textAlign: "left",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "relative",
          width: 42,
          height: 22,
          flex: "0 0 42px",
          border: "1px solid",
          borderColor: checked ? "primary.main" : "divider",
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 3,
            left: checked ? 22 : 3,
            width: 14,
            height: 14,
            bgcolor: checked ? "primary.main" : "text.secondary",
            transition: "left 140ms cubic-bezier(.76,0,.24,1)",
          }}
        />
      </Box>
      <Typography sx={{ fontSize: compact ? ".72rem" : ".78rem", fontWeight: 900 }}>{label}</Typography>
    </ButtonBase>
    {detail && (
      <Typography sx={{ mt: 0.35, color: "text.secondary", fontSize: ".68rem", lineHeight: 1.4 }}>{detail}</Typography>
    )}
  </Box>
);
